import { useTranslation } from 'react-i18next';
import { useParams, useSearchParams } from 'react-router-dom';
import { Roles } from '@/auth/roles';
import { useCan } from '@/auth/useAuth';
import { PageHeader } from '@/components/layout/PageHeader';
import { Badge, Skeleton } from '@/components/ui/card';
import { Tabs } from '@/components/ui/form-controls';
import { useTenant } from './api';
import { CapsTab, ModelPolicyTab } from './CapsTabs';
import { ChannelsTab } from './ChannelsTab';
import { GeneralTab } from './GeneralTab';
import { ConsentsTab } from './ConsentsTab';
import { JdeTab } from './JdeTab';
import { SettingsTab } from './SettingsTab';
import { TestModeBadge } from './TestModeBadge';
import { UsersTab } from './UsersTab';

const TABS = ['general', 'jde', 'channels', 'consents', 'users', 'caps', 'modelPolicy', 'settings'] as const;
type TabId = (typeof TABS)[number];
const PLATFORM_ONLY: readonly TabId[] = ['general', 'caps', 'modelPolicy'];

/** /tenants/:id — tenant administration (08 §1, 07 §2.1–2.2). Tenant admins see their own tenant without platform-only tabs. */
export function TenantDetailPage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const isPlatformAdmin = useCan({ roles: [Roles.PlatformAdmin] });
  const tenant = useTenant(id);
  const [params, setParams] = useSearchParams();

  const visible = TABS.filter((tab) => isPlatformAdmin || !PLATFORM_ONLY.includes(tab));
  const requested = params.get('tab') as TabId | null;
  const tab: TabId = requested && visible.includes(requested) ? requested : (visible[0] ?? 'channels');

  if (tenant.isPending) {
    return (
      <div className="p-6">
        <Skeleton className="h-8 w-64" />
      </div>
    );
  }
  if (!tenant.data) return <div className="p-6 text-sm text-muted-foreground">{t('admin.tenants.notFound')}</div>;
  const tn = tenant.data;

  return (
    <div>
      <PageHeader
        title={tn.name}
        description={`${tn.slug} · ${tn.timezone} · ${tn.locales.join(', ')}`}
        help="tenant"
        actions={
          <div className="flex items-center gap-2">
            <TestModeBadge count={tn.testRecipientCount} />
            <Badge variant={tn.status === 'active' ? 'success' : 'destructive'}>{t(`admin.tenantStatus.${tn.status}`)}</Badge>
          </div>
        }
      />
      <Tabs
        items={visible.map((v) => ({ id: v, label: t(`admin.tenantTabs.${v}`) }))}
        value={tab}
        onChange={(v) => setParams({ tab: v }, { replace: true })}
      />
      <div className="max-w-4xl p-6">
        {tab === 'general' && <GeneralTab tenant={tn} />}
        {tab === 'jde' && <JdeTab tenant={tn} canEditCredentials={isPlatformAdmin} />}
        {tab === 'channels' && <ChannelsTab tenantId={tn.id} canTestSend={isPlatformAdmin} locales={tn.locales} />}
        {tab === 'consents' && <ConsentsTab tenantId={tn.id} />}
        {tab === 'users' && <UsersTab tenantId={tn.id} />}
        {tab === 'caps' && <CapsTab tenant={tn} />}
        {tab === 'modelPolicy' && <ModelPolicyTab tenant={tn} />}
        {tab === 'settings' && <SettingsTab tenantId={tn.id} />}
      </div>
    </div>
  );
}
